import type { Dispatch, FC, SetStateAction } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import type { SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import type { z } from 'zod';

import type { SMSTemplate } from '@polyai/stream/store/reducers/sms';
import {
  createTemplate,
  smsReferences,
  updateTemplate,
} from '@polyai/stream/store/reducers/sms';
import { selectActiveSmsTemplates } from '@polyai/stream/store/reducers/sms/selectors';
import { getPromptRefs } from '@polyai/stream/utils/get-prompt-refs';
import { ConfirmationModal } from '@polyai/ui/components/molecules/ConfirmationModal';

import FormProvider from 'components/molecules/Form/FormProvider';
import UnsavedChangesModal from 'components/molecules/UnsavedChangesModal';
import SmsTemplateFormContent from 'components/organisms/SmsTemplatesModal/SmsTemplateFormContent';
import { useAppSelector } from 'hooks/reduxHooks';
import { getActionName } from 'lib/ddlogHelper';
import { generateSmsTemplateId } from 'lib/prettyUuid';
import { NewSMSSchema } from 'utils/validators/sms';
import { useServerDispatch } from '../AgentStream/context/AgentStreamContext';
import { SMS_TEMPLATES_MODAL } from './SmsTemplatesModal.uiCopy';

interface Props {
  open: boolean;
  isRequesting: boolean;
  onClose?: () => void;
  template?: SMSTemplate;
  isEdit?: boolean;
  readonly?: boolean;
  onUpsertSuccess?: (
    newTemplateName: string,
    existingTemplateName?: string,
    id?: string,
  ) => void;
  onUpsertError?: () => void;
  setIsOpen?: Dispatch<SetStateAction<boolean>>;
}

const Schema = NewSMSSchema;

type SchemaProps = z.input<typeof Schema>;

export type FormProps = z.output<typeof Schema>;

const NewSmsTemplatesModal: FC<Props> = ({
  open,
  setIsOpen,
  isRequesting,
  onClose,
  template,
  isEdit = false,
  readonly = false,
  onUpsertSuccess,
  onUpsertError,
}) => {
  const serverDispatch = useServerDispatch();
  const activeSmsTemplates = useAppSelector(selectActiveSmsTemplates);
  const [isUnsavedChangesModalOpen, setIsUnsavedChangesOpen] = useState(false);

  const UniqueNameSchema = Schema.refine(
    value => {
      return !activeSmsTemplates.some(
        v => v.id !== defaultValues.id && v.name === value.name,
      );
    },
    {
      path: ['name'],
      message: SMS_TEMPLATES_MODAL.form.title.duplicateError,
    },
  );

  const defaultValues = useMemo(() => {
    const liveNumber = template
      ? {
          value: template?.envPhoneNumbers?.live,
          label: template?.envPhoneNumbers?.live,
        }
      : undefined;
    return {
      ...template,
      envPhoneNumbers: liveNumber,
    };
  }, [template]);

  const methods = useForm<SchemaProps, unknown, FormProps>({
    resolver: zodResolver(UniqueNameSchema),
    defaultValues: defaultValues,
    mode: 'onSubmit',
    shouldFocusError: false,
  });

  const {
    handleSubmit,
    reset,
    formState: { isDirty },
    getValues,
  } = methods;

  // the form won’t update automatically if defaultValues is loaded async
  // or changes after first render
  useEffect(() => {
    if (defaultValues && !isDirty) {
      reset(defaultValues);
    }
  }, [defaultValues, reset, isDirty]);

  const handleCloseModal = useCallback(() => {
    setIsOpen?.(false);
    onClose?.();
  }, [onClose, setIsOpen]);

  const handleOnClose = () => {
    handleCloseModal();
    if (isDirty) {
      setIsUnsavedChangesOpen(true);
    }
  };

  const onSuccess = (id: string) => {
    const newTemplateName = getValues('name');
    onUpsertSuccess?.(newTemplateName, template?.name, id);

    reset(defaultValues);

    handleCloseModal();
  };

  const onSubmit: SubmitHandler<FormProps> = async data => {
    if (isEdit) {
      if (!template?.id) {
        throw new Error('Template ID is required');
      }
      try {
        await serverDispatch(
          updateTemplate({
            ...data,
            id: template.id,
            references: getPromptRefs(
              data.text || '',
              smsReferences.createInitialState(),
            ),
          }),
        );
        onSuccess(template.id);
      } catch {
        onUpsertError?.();
      }
    } else {
      const id = generateSmsTemplateId();
      try {
        await serverDispatch(
          createTemplate({
            id,
            ...data,
            active: true,
            references: getPromptRefs(
              data.text || '',
              smsReferences.createInitialState(),
            ),
          }),
        );
        onSuccess(id);
      } catch {
        onUpsertError?.();
      }
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing deps are intentional
  const handleDiscardChanges = useCallback(() => {
    setIsUnsavedChangesOpen(false);
    reset(defaultValues);
    handleCloseModal();
  }, [handleCloseModal, reset, setIsUnsavedChangesOpen, defaultValues]);

  const handleKeepChanges = useCallback(() => {
    setIsUnsavedChangesOpen(false);
    setIsOpen?.(true);
  }, [setIsOpen]);

  const handleUnsavedChangesModalOpen = useCallback(() => {
    setIsOpen?.(false);
  }, [setIsOpen]);

  return (
    <>
      <UnsavedChangesModal
        cancel={{
          onClick: handleKeepChanges,
        }}
        confirm={{
          onClick: handleDiscardChanges,
        }}
        open={isUnsavedChangesModalOpen}
        onOpenChange={handleUnsavedChangesModalOpen}
      />
      <ConfirmationModal
        buttonText={
          isEdit
            ? SMS_TEMPLATES_MODAL.edit.button.confirm
            : readonly
              ? SMS_TEMPLATES_MODAL.view.button.confirm
              : SMS_TEMPLATES_MODAL.add.button.confirm
        }
        confirmDisabled={!isDirty && isEdit}
        confirmRequesting={isRequesting}
        content={
          <FormProvider methods={methods} onSubmit={onSubmit}>
            <SmsTemplateFormContent readonly={readonly} />
          </FormProvider>
        }
        hasCancelButton={!readonly}
        open={open}
        size="md"
        subtitle={SMS_TEMPLATES_MODAL.description}
        title={
          isEdit
            ? SMS_TEMPLATES_MODAL.edit.title
            : readonly
              ? SMS_TEMPLATES_MODAL.view.title
              : SMS_TEMPLATES_MODAL.add.title
        }
        nested
        skipCloseAndConfirm
        onCancelButtonClick={handleOnClose}
        onCancelDDActionName={getActionName(
          'SMS',
          'cancel',
          isEdit ? 'edit' : 'create',
        )}
        onClose={handleOnClose}
        onConfirmButtonClick={readonly ? handleOnClose : handleSubmit(onSubmit)}
      />
    </>
  );
};

export default NewSmsTemplatesModal;
